import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import './SchoolDashboard.css';

function SchoolChat() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [chatConversations, setChatConversations] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatMessagesLoading, setChatMessagesLoading] = useState(false);
  const { selectedBranchId } = useBranchFilter();
  const messagesEndRef = useRef(null);
  const conversationsChannelRef = useRef(null);
  const messagesChannelRef = useRef(null);

  useEffect(() => {
    // Check if user is authenticated
    const isAuthenticated = localStorage.getItem('schoolAuthenticated');
    const storedSchoolData = localStorage.getItem('schoolData');
    
    if (!isAuthenticated || !storedSchoolData) {
      navigate('/schoollogin');
      return;
    }
    
    setSchoolData(JSON.parse(storedSchoolData));
    setLoading(false);
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      localStorage.removeItem('schoolAuthenticated');
      localStorage.removeItem('schoolData');
      localStorage.removeItem('supabaseAuthToken');
      navigate('/schoollogin');
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.removeItem('schoolAuthenticated');
      localStorage.removeItem('schoolData');
      localStorage.removeItem('supabaseAuthToken');
      navigate('/schoollogin');
    }
  };

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  // Fetch chat conversations (groups with student-teacher chats for this school)
  const fetchChatConversations = async () => {
    if (!schoolData?.id) return;

    try {
      setChatLoading(true);
      
      const { data: groups, error: groupsError } = await supabase
        .from('chat_groups')
        .select('*')
        .order('created_at', { ascending: false });

      if (groupsError) throw groupsError;

      const conversations = [];
      for (const group of groups || []) {
        const { data: members, error: membersError } = await supabase
          .from('chat_group_members')
          .select('*')
          .eq('group_id', group.id);

        if (membersError) continue;

        const hasStudent = members.some(m => m.user_type === 'student');
        const hasTeacher = members.some(m => m.user_type === 'teacher');

        if (hasStudent && hasTeacher) {
          const studentMember = members.find(m => m.user_type === 'student');
          const teacherMember = members.find(m => m.user_type === 'teacher');

          let studentName = studentMember?.display_name || 'Student';
          let studentSchoolId = null;
          if (studentMember?.user_id) {
            const { data: student } = await supabase
              .from('students')
              .select('id, first_name, last_name, school_id, branch_id')
              .eq('id', studentMember.user_id)
              .single();
            if (student) {
              studentName = `${student.first_name} ${student.last_name}`;
              studentSchoolId = student.school_id;
              
              if (selectedBranchId && student.branch_id !== selectedBranchId) {
                continue;
              }
            } else {
              continue;
            }
          } else {
            continue;
          }

          if (studentSchoolId !== schoolData.id) {
            continue;
          }

          let teacherName = teacherMember?.display_name || 'Teacher';
          if (teacherMember?.user_id) {
            const { data: teacher } = await supabase
              .from('teachers')
              .select('id, first_name, last_name')
              .eq('id', teacherMember.user_id)
              .single();
            if (teacher) {
              teacherName = `${teacher.first_name} ${teacher.last_name}`;
            }
          }

          const { data: latestMessage } = await supabase
            .from('chat_messages')
            .select('*')
            .eq('group_id', group.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .single();

          conversations.push({
            id: group.id,
            name: `${studentName} - ${teacherName}`,
            student_name: studentName,
            teacher_name: teacherName,
            student_id: studentMember?.user_id,
            teacher_id: teacherMember?.user_id,
            last_message_content: latestMessage?.content || null,
            last_message_time: latestMessage?.created_at || null
          });
        }
      }

      conversations.sort((a, b) => {
        if (!a.last_message_time) return 1;
        if (!b.last_message_time) return -1;
        return new Date(b.last_message_time) - new Date(a.last_message_time);
      });

      setChatConversations(conversations);
    } catch (error) {
      console.error('Error fetching chat conversations:', error);
    } finally {
      setChatLoading(false);
    }
  };

  // Fetch messages for selected conversation
  const fetchConversationMessages = async (groupId) => {
    try {
      setChatMessagesLoading(true);
      const { data: messages, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('group_id', groupId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const enrichedMessages = await Promise.all(
        (messages || []).map(async (message) => {
          let senderName = 'Unknown';
          
          if (message.sender_type === 'student' && message.sender_id) {
            const { data: student } = await supabase
              .from('students')
              .select('first_name, last_name')
              .eq('id', message.sender_id)
              .single();
            if (student) {
              senderName = `${student.first_name} ${student.last_name}`;
            }
          } else if (message.sender_type === 'teacher' && message.sender_id) {
            const { data: teacher } = await supabase
              .from('teachers')
              .select('first_name, last_name')
              .eq('id', message.sender_id)
              .single();
            if (teacher) {
              senderName = `${teacher.first_name} ${teacher.last_name}`;
            }
          }

          return {
            ...message,
            sender_name: senderName,
            student_name: message.sender_type === 'student' ? senderName : null,
            teacher_name: message.sender_type === 'teacher' ? senderName : null
          };
        })
      );

      setChatMessages(enrichedMessages);
      scrollToBottom();
    } catch (error) {
      console.error('Error fetching conversation messages:', error);
    } finally {
      setChatMessagesLoading(false);
    }
  };

  // Set up real-time subscription for conversations
  useEffect(() => {
    if (!schoolData?.id) return;

    // Initial fetch
    fetchChatConversations();

    // Clean up previous channel
    if (conversationsChannelRef.current) {
      supabase.removeChannel(conversationsChannelRef.current);
    }

    // Set up real-time subscription for new messages (which updates last message in conversations)
    const channel = supabase
      .channel('school-chat-conversations')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'chat_messages'
      }, (payload) => {
        // When a new message is added, refresh conversations to update last message
        fetchChatConversations();
      })
      .subscribe();

    conversationsChannelRef.current = channel;

    return () => {
      if (conversationsChannelRef.current) {
        supabase.removeChannel(conversationsChannelRef.current);
        conversationsChannelRef.current = null;
      }
    };
  }, [schoolData?.id, selectedBranchId]);

  // Set up real-time subscription for messages in selected conversation
  useEffect(() => {
    if (!selectedConversation) {
      // Clean up messages channel when no conversation is selected
      if (messagesChannelRef.current) {
        supabase.removeChannel(messagesChannelRef.current);
        messagesChannelRef.current = null;
      }
      setChatMessages([]);
      return;
    }

    // Initial fetch
    fetchConversationMessages(selectedConversation.id);

    // Clean up previous channel
    if (messagesChannelRef.current) {
      supabase.removeChannel(messagesChannelRef.current);
    }

    // Set up real-time subscription for new messages in this conversation
    const channel = supabase
      .channel(`school-chat-messages-${selectedConversation.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'chat_messages',
        filter: `group_id=eq.${selectedConversation.id}`
      }, async (payload) => {
        const newMessage = payload.new;
        
        // Check if message already exists
        setChatMessages(prev => {
          const exists = prev.some(m => m.id === newMessage.id);
          if (exists) return prev;
          return prev;
        });

        // Enrich the new message with sender name
        let senderName = 'Unknown';
        
        if (newMessage.sender_type === 'student' && newMessage.sender_id) {
          const { data: student } = await supabase
            .from('students')
            .select('first_name, last_name')
            .eq('id', newMessage.sender_id)
            .single();
          if (student) {
            senderName = `${student.first_name} ${student.last_name}`;
          }
        } else if (newMessage.sender_type === 'teacher' && newMessage.sender_id) {
          const { data: teacher } = await supabase
            .from('teachers')
            .select('first_name, last_name')
            .eq('id', newMessage.sender_id)
            .single();
          if (teacher) {
            senderName = `${teacher.first_name} ${teacher.last_name}`;
          }
        }

        setChatMessages(prev => {
          const exists = prev.some(m => m.id === newMessage.id);
          if (exists) return prev;
          
          const enrichedMessage = {
            ...newMessage,
            sender_name: senderName,
            student_name: newMessage.sender_type === 'student' ? senderName : null,
            teacher_name: newMessage.sender_type === 'teacher' ? senderName : null
          };
          
          const updated = [...prev, enrichedMessage].sort((a, b) => 
            new Date(a.created_at) - new Date(b.created_at)
          );
          
          setTimeout(() => scrollToBottom(), 100);
          return updated;
        });
      })
      .subscribe();

    messagesChannelRef.current = channel;

    return () => {
      if (messagesChannelRef.current) {
        supabase.removeChannel(messagesChannelRef.current);
        messagesChannelRef.current = null;
      }
    };
  }, [selectedConversation]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!schoolData) {
    return (
      <div className="loading-container">
        <p>No school data found. Please log in again.</p>
        <button onClick={() => navigate('/schoollogin')}>
          Go to Login
        </button>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
      
      <div className="main-content">
        <div className="content-header">
          <h1>💬 Chat</h1>
          <p>View conversations between students and teachers</p>
        </div>

        <div className="content-body" style={{ padding: '0' }}>
          <div style={{ 
            display: 'flex', 
            height: 'calc(100vh - 180px)', 
            backgroundColor: '#F9FAFB',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            {/* Conversations Sidebar */}
            <div style={{
              width: '350px',
              backgroundColor: '#FFFFFF',
              borderRight: '1px solid #E5E7EB',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              <div style={{
                padding: '24px',
                borderBottom: '1px solid #E5E7EB',
                backgroundColor: '#F9FAFB'
              }}>
                <h2 style={{ 
                  margin: 0, 
                  fontSize: '20px', 
                  fontWeight: '700', 
                  color: '#1F2937' 
                }}>
                  Conversations
                </h2>
                <p style={{ 
                  margin: '8px 0 0 0', 
                  fontSize: '14px', 
                  color: '#6B7280' 
                }}>
                  {chatConversations.length} {chatConversations.length === 1 ? 'conversation' : 'conversations'}
                </p>
              </div>
              
              <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: '8px'
              }}>
                {chatLoading ? (
                  <div style={{ 
                    padding: '40px', 
                    textAlign: 'center', 
                    color: '#6B7280' 
                  }}>
                    <div className="loading-spinner" style={{ margin: '0 auto 16px' }}></div>
                    <p>Loading conversations...</p>
                  </div>
                ) : chatConversations.length > 0 ? (
                  chatConversations.map(conv => (
                    <div
                      key={conv.id}
                      onClick={() => handleSelectConversation(conv)}
                      style={{
                        padding: '16px',
                        marginBottom: '8px',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        backgroundColor: selectedConversation?.id === conv.id ? '#EFF6FF' : '#FFFFFF',
                        border: selectedConversation?.id === conv.id ? '2px solid #3B82F6' : '1px solid #E5E7EB',
                        boxShadow: selectedConversation?.id === conv.id ? '0 2px 8px rgba(59, 130, 246, 0.15)' : '0 1px 3px rgba(0, 0, 0, 0.05)'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedConversation?.id !== conv.id) {
                          e.currentTarget.style.backgroundColor = '#F9FAFB';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedConversation?.id !== conv.id) {
                          e.currentTarget.style.backgroundColor = '#FFFFFF';
                        }
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '8px'
                      }}>
                        <h3 style={{
                          margin: 0,
                          fontSize: '15px',
                          fontWeight: '600',
                          color: '#1F2937',
                          lineHeight: '1.4'
                        }}>
                          {conv.name}
                        </h3>
                      </div>
                      {conv.last_message_content && (
                        <p style={{
                          margin: '4px 0 0 0',
                          fontSize: '13px',
                          color: '#6B7280',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '100%'
                        }}>
                          {conv.last_message_content}
                        </p>
                      )}
                      {conv.last_message_time && (
                        <span style={{
                          fontSize: '11px',
                          color: '#9CA3AF',
                          marginTop: '8px',
                          display: 'block'
                        }}>
                          {formatTime(conv.last_message_time)}
                        </span>
                      )}
                    </div>
                  ))
                ) : (
                  <div style={{ 
                    padding: '40px', 
                    textAlign: 'center', 
                    color: '#6B7280' 
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>💭</div>
                    <p style={{ margin: 0, fontSize: '14px' }}>No active conversations</p>
                  </div>
                )}
              </div>
            </div>

            {/* Chat Messages Area */}
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#FFFFFF',
              overflow: 'hidden'
            }}>
              {selectedConversation ? (
                <>
                  <div style={{
                    padding: '20px 24px',
                    borderBottom: '1px solid #E5E7EB',
                    backgroundColor: '#F9FAFB'
                  }}>
                    <h3 style={{
                      margin: 0,
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#1F2937'
                    }}>
                      {selectedConversation.name}
                    </h3>
                    <p style={{
                      margin: '4px 0 0 0',
                      fontSize: '13px',
                      color: '#6B7280'
                    }}>
                      {selectedConversation.student_name} ↔ {selectedConversation.teacher_name}
                    </p>
                  </div>
                  
                  <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '24px',
                    backgroundColor: '#F9FAFB',
                    backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(0,0,0,0.03) 1px, transparent 0)',
                    backgroundSize: '20px 20px'
                  }}>
                    {chatMessagesLoading ? (
                      <div style={{ 
                        textAlign: 'center', 
                        padding: '40px', 
                        color: '#6B7280' 
                      }}>
                        <div className="loading-spinner" style={{ margin: '0 auto 16px' }}></div>
                        <p>Loading messages...</p>
                      </div>
                    ) : chatMessages.length > 0 ? (
                      <>
                        {chatMessages.map(message => (
                          <div 
                            key={message.id} 
                            style={{
                              marginBottom: '16px',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: message.sender_type === 'student' ? 'flex-start' : 'flex-end'
                            }}
                          >
                            <div style={{
                              maxWidth: '70%',
                              padding: '12px 16px',
                              borderRadius: '16px',
                              backgroundColor: message.sender_type === 'student' ? '#FFFFFF' : '#3B82F6',
                              color: message.sender_type === 'student' ? '#1F2937' : '#FFFFFF',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                              border: message.sender_type === 'student' ? '1px solid #E5E7EB' : 'none'
                            }}>
                              <div style={{
                                fontSize: '12px',
                                fontWeight: '600',
                                marginBottom: '6px',
                                opacity: 0.8
                              }}>
                                {message.sender_name}
                              </div>
                              <p style={{
                                margin: 0,
                                fontSize: '15px',
                                lineHeight: '1.5',
                                wordWrap: 'break-word'
                              }}>
                                {message.content}
                              </p>
                              <div style={{
                                fontSize: '11px',
                                marginTop: '8px',
                                opacity: 0.7,
                                textAlign: 'right'
                              }}>
                                {formatTime(message.created_at)}
                              </div>
                            </div>
                          </div>
                        ))}
                        <div ref={messagesEndRef} />
                      </>
                    ) : (
                      <div style={{ 
                        textAlign: 'center', 
                        padding: '60px 40px', 
                        color: '#6B7280' 
                      }}>
                        <div style={{ fontSize: '64px', marginBottom: '16px' }}>💬</div>
                        <p style={{ margin: 0, fontSize: '16px', fontWeight: '500' }}>
                          No messages yet
                        </p>
                        <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
                          Start the conversation!
                        </p>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  color: '#6B7280'
                }}>
                  <div style={{ fontSize: '80px', marginBottom: '24px' }}>💬</div>
                  <h3 style={{ 
                    margin: 0, 
                    fontSize: '20px', 
                    fontWeight: '600', 
                    color: '#1F2937',
                    marginBottom: '8px'
                  }}>
                    Select a conversation
                  </h3>
                  <p style={{ margin: 0, fontSize: '14px' }}>
                    Choose a conversation from the sidebar to view messages
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SchoolChat;
